import HelperFunctions from 'pages/global/HelperFunctions';
import Parser from 'html-react-parser';
import InteractivePart from 'pages/documents/misc/_BlockParser/InteractivePart';
import { Fragment } from 'react';

export function getTable(block, markChanges = false) {
    let table;
    let tableJson = block.latestContent;

    if (markChanges && block.hasOwnProperty('renderedDiffContent') && block.renderedDiffContent !== '') {
        tableJson = block.renderedDiffContent;
    }

    table = HelperFunctions.tryParseJSON(tableJson);

    if (table !== false) {
        return table;
    }

    return {
        data: {},
        templateId: false,
        title: '',
    };
}

export function getTableDataSet(table, variantId) {
    // this.table.data is an array with datasets for all variants
    return HelperFunctions.getByValue(table.data, 'variantId', variantId);
}

export function getColumns(template, packageGroupId, variantId, document) {
    if (!template?.columns) {
        // Probably loading not complete yet
        return [];
    }

    let columns = [];

    switch (template.columns) {
        case 'packages':
            // get columns from chosen set of packages
            if (packageGroupId) {
                const packageGroup = getPackageGroup(packageGroupId, document.packageGroups);
                if (packageGroup && packageGroup.hasOwnProperty('packages') && packageGroup.packages.length > 0) {
                    columns = packageGroup.packages;
                }
            }
            break;
        default:
            columns = template.columns.length > 0 ? template.columns : [];
            columns = prepareTags(columns, 'name', variantId, document);
            break;
    }

    return columns;
}

export function prepareTags(items = [], key, variantId, document) {
    let itemsCopy = [];
    if (items.length > 0) {
        itemsCopy = items.map((item) => {
            const itemCopy = Object.assign({}, item);
            itemCopy[key] = HelperFunctions.prepareTags(itemCopy[key], document, true);
            return itemCopy;
        });
    }

    return itemsCopy;
}

export function validateData(table, dataSet, rows, columns) {
    let templateIsValid = true;
    let rowsAreValid = true;
    let colsAreValid = true;
    let tableHasData = true;
    let dataSetHasData = true;

    if (!table.templateId) {
        templateIsValid = false;
    }

    if (rows === undefined || rows.length < 1) {
        rowsAreValid = false;
    }
    if (columns === undefined || !columns || columns.length < 1) {
        colsAreValid = false;
    }

    if (table.data === undefined || table.data.length < 1) {
        tableHasData = false;
    }

    if (!dataSet.hasOwnProperty('rows')) {
        dataSetHasData = false;
    }

    return { colsAreValid, dataSetHasData, rowsAreValid, tableHasData, templateIsValid };
}

export function prepareTableData(dataSet, templateRows, columns, blockDisplayOptions, block, selectedPart, selectPart) {
    let rows = [];

    if (dataSet !== undefined && dataSet.hasOwnProperty('rows') && dataSet.rows.length > 0) {
        rows = dataSet.rows;
    } else {
        // no data stored, preparing an empty set based on the template
        if (templateRows !== undefined && templateRows.length > 0) {
            rows = templateRows.map((templateRow) => {
                return {
                    id: templateRow.id,
                    cells: [],
                };
            });
        }
    }

    if (rows.length === 0) {
        return dataSet;
    }

    return {
        ...dataSet,
        rows: rows.map((row) => {
            // make sure every row has a cells array and each cell has an entityId and value
            let cells = row.hasOwnProperty('cells') || row.cells.length > 0 ? row.cells : [];

            // make sure there is a cell for each column
            if (columns && columns.length > 0) {
                columns.forEach((column) => {
                    let cellFound = false;
                    const newCells = cells.map((cell) => {
                        if (blockDisplayOptions.table.interactiveChanges) {
                            // prepare interactive content
                            let cellContent = cell.diffContent === '' ? cell.latestContent : cell.diffContent;
                            cell.latestContent = prepareInteractiveContent(
                                cellContent,
                                block.changes,
                                block.id,
                                selectedPart,
                                selectPart,
                            );
                            cell.useParser = false;
                        } else if (blockDisplayOptions.table.markChanges) {
                            cell.latestContent = cell.diffContent === '' ? cell.latestContent : cell.diffContent;
                            cell.useParser = true;
                        } else {
                            cell.useParser = true;
                        }

                        if (cell.entityId === column.id) {
                            cellFound = true;
                        }

                        return cell;
                    });

                    if (!cellFound) {
                        // no cell found for this column
                        const cell = {
                            entityId: column.id,
                            latestContent: '',
                            diffContent: '',
                        };
                        newCells.push(cell);
                    }

                    cells = newCells;
                });
            }

            row.cells = cells;
            return row;
        }),
    };
}

function prepareInteractiveContent(content, changes, blockId, selectedPart, selectPart) {
    if (typeof content !== 'string') {
        return content;
    }

    return Parser(content, {
        replace: (domNode) => {
            if (
                domNode.name === 'change' &&
                domNode.hasOwnProperty('attribs') &&
                domNode.attribs.hasOwnProperty('data-diff-id')
            ) {
                const domNodeId = parseInt(domNode.attribs['data-diff-id']);
                let part = {};
                changes.forEach((partItem) => {
                    if (partItem.diffId === domNodeId) {
                        part = partItem;
                    }
                });
                if (part !== undefined) {
                    return (
                        <InteractivePart
                            part={part}
                            selectPart={() => selectPart(part.id, blockId)}
                            isSelected={selectedPart.block === blockId && selectedPart.part === part.id}
                        />
                    );
                } else {
                    return <Fragment />;
                }
            } else if (domNode.name === 'change') {
                return <Fragment />;
            }
        },
    });
}

function getPackageGroup(packageGroupId, packageGroups = []) {
    if (!packageGroupId || packageGroups === undefined || packageGroups.length < 1) {
        return false;
    }

    const packageGroup = HelperFunctions.getByValue(packageGroups, 'id', packageGroupId);

    if (packageGroup !== undefined) {
        return packageGroup;
    }

    return false;
}